import React, { useState, useEffect } from 'react';
import './Leaderboard.css';
import guestSessionService from '../services/guestSessionService';
import { useAuth } from '../contexts/AuthContext';

interface LeaderboardEntry {
  rank: number;
  name: string;
  score: number;
  gamesPlayed: number;
  perfectScores: number;
  avatar?: string;
}

interface LeaderboardProps {
  currentUserScore?: number;
  currentUserName?: string;
}

const Leaderboard: React.FC<LeaderboardProps> = ({ currentUserScore = 0, currentUserName = 'Guest' }) => {
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardEntry[]>([]);
  const [timeFilter, setTimeFilter] = useState<'today' | 'week' | 'all'>('week');
  const [loading, setLoading] = useState(false);

  const { user } = useAuth();
  const guestSession = guestSessionService.getSession();
  const guestScore = guestSession?.totalScore || 0;

  useEffect(() => {
    // Placeholder data for now - will be replaced with actual API call
    const mockData: LeaderboardEntry[] = [
      { rank: 1, name: 'Champion', score: 450, gamesPlayed: 12, perfectScores: 8 },
      { rank: 2, name: 'Star Player', score: 380, gamesPlayed: 10, perfectScores: 5 },
      { rank: 3, name: 'Rising Star', score: 320, gamesPlayed: 8, perfectScores: 3 },
      { rank: 4, name: 'Learner Pro', score: 280, gamesPlayed: 7, perfectScores: 2 },
      { rank: 5, name: 'Word Master', score: 250, gamesPlayed: 6, perfectScores: 1 },
    ];

    // Simulate loading
    setLoading(true);
    setTimeout(() => {
      setLeaderboardData(mockData);
      setLoading(false);
    }, 500);
  }, [timeFilter]);

  return (
    <div className="leaderboard-container">
      <div className="leaderboard-header">
        <h2>🏆 Leaderboard</h2>
        <div className="time-filter">
          <button
            className={timeFilter === 'today' ? 'active' : ''}
            onClick={() => setTimeFilter('today')}
          >
            Today
          </button>
          <button
            className={timeFilter === 'week' ? 'active' : ''}
            onClick={() => setTimeFilter('week')}
          >
            This Week
          </button>
          <button
            className={timeFilter === 'all' ? 'active' : ''}
            onClick={() => setTimeFilter('all')}
          >
            All Time
          </button>
        </div>
      </div>

      {loading ? (
        <div className="leaderboard-loading">
          <div className="spinner"></div>
          <p>Loading leaderboard...</p>
        </div>
      ) : (
        <>
          {leaderboardData.length === 0 ? (
            <div className="leaderboard-empty">
              <p>🎮 No scores yet!</p>
              <p>Be the first to play and set a high score!</p>
            </div>
          ) : (
            <div className="leaderboard-table">
              <div className="table-header">
                <div className="rank-col">Rank</div>
                <div className="player-col">Player</div>
                <div className="score-col">Score</div>
                <div className="games-col">Games</div>
                <div className="perfect-col">Perfect</div>
              </div>
              <div className="table-body">
                {leaderboardData.map((entry, index) => {
                  // Check if guest score would be inserted here
                  const shouldShowGuestHere = !user && guestScore > 0 &&
                    ((index === 0 && guestScore > entry.score) ||
                     (index > 0 && guestScore <= leaderboardData[index - 1].score && guestScore > entry.score));

                  return (
                    <React.Fragment key={entry.rank}>
                      {shouldShowGuestHere && (
                        <div className="leaderboard-row guest-position">
                          <div className="rank-col">👤</div>
                          <div className="player-col">
                            <span className="player-name">You (not signed in)</span>
                          </div>
                          <div className="score-col">
                            <span className="score-value">{guestScore}</span>
                          </div>
                          <div className="games-col">{guestSession?.gamesPlayed || 0}</div>
                          <div className="perfect-col">
                            {(guestSession?.perfectScores || 0) > 0 && `⭐ ${guestSession?.perfectScores}`}
                          </div>
                        </div>
                      )}
                      <div
                        className={`leaderboard-row ${entry.name === currentUserName ? 'current-user' : ''}`}
                      >
                        <div className="rank-col">
                          {entry.rank === 1 && '🥇'}
                          {entry.rank === 2 && '🥈'}
                          {entry.rank === 3 && '🥉'}
                          {entry.rank > 3 && entry.rank}
                        </div>
                        <div className="player-col">
                          <span className="player-name">{entry.name}</span>
                        </div>
                        <div className="score-col">
                          <span className="score-value">{entry.score}</span>
                        </div>
                        <div className="games-col">{entry.gamesPlayed}</div>
                        <div className="perfect-col">
                          {entry.perfectScores > 0 && `⭐ ${entry.perfectScores}`}
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}

                {/* Show guest at the end if they haven't beaten any scores */}
                {!user && guestScore > 0 && guestScore <= leaderboardData[leaderboardData.length - 1].score && (
                  <div className="leaderboard-row guest-position">
                    <div className="rank-col">👤</div>
                    <div className="player-col">
                      <span className="player-name">You (not signed in)</span>
                    </div>
                    <div className="score-col">
                      <span className="score-value">{guestScore}</span>
                    </div>
                    <div className="games-col">{guestSession?.gamesPlayed || 0}</div>
                    <div className="perfect-col">
                      {(guestSession?.perfectScores || 0) > 0 && `⭐ ${guestSession?.perfectScores}`}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {currentUserScore > 0 && (
            <div className="user-stats">
              <h3>Your Stats</h3>
              <div className="stats-row">
                <span>Current Score: {currentUserScore}</span>
                <span>Rank: #{leaderboardData.findIndex(e => e.score < currentUserScore) + 1 || leaderboardData.length + 1}</span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Leaderboard;